import ValidationHelper from 'legacy/lut/classes/helpers/partner360/ValidationHelper'
import { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { FC, MouseEvent, useCallback, useMemo } from 'react'

import { FormDropdown } from 'cng-common/features/form'
import { ValidationType } from 'cng-common/features/form/configs'
import { StringKeys } from 'cng-common/models/utils'
import { IForm } from 'core-components/classes/models/IForm'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { getKeyObjectiveExpansionKeys } from 'partner360/features/keyInitiativeEditor/lib/getKeyObjectiveExpansionKeys'

import { transformKeyObjectivesDataToHierarchyFormat } from '../../../../helpers/transformKeyObjectivesDataToHierarchyFormat'
import { IHierarchyKeyObjectives } from '../../../../lpKeyInitiatives/components/keyInitiativeEditorModal/models/hierarchyKeyObjectives'

import { isKeyObjectiveSelectable as getIsKeyObjectiveSelectable } from './isKeyObjectiveSelectable'
import { KeyObjectiveItem } from './KeyObjectiveItem'

const NO_EXPANSION: readonly string[] = []

interface IKeyObjectivesProps {
  initialValue?: string
  selectedValue?: string
  isDataInsightTab?: boolean
  isAmplifyImpactInfoSection?: boolean
  keyObjectivesSettingsList: IKeyObjective[]
  programType?: ProgramTypes
  keyObjectivesFilter?: string
  isDisabled?: boolean
  className?: string
  formKey: string
  onChange?: (keyObjective: string) => void
  caption?: StringKeys<IHierarchyKeyObjectives>
  label?: string
  placeholder?: string
  selectOnlyLeaves?: boolean
  singleRootExpansion?: 'root' | 'leaves'
}

export const KeyObjectives: FC<IKeyObjectivesProps> = ({
  initialValue = '',
  selectedValue,
  formKey,
  keyObjectivesSettingsList,
  programType,
  isDisabled,
  className,
  keyObjectivesFilter,
  onChange,
  isDataInsightTab,
  isAmplifyImpactInfoSection,
  caption,
  label,
  placeholder,
  selectOnlyLeaves = false,
  singleRootExpansion,
}) => {
  const keyObjectivesWithHierarchy = useMemo(() => {
    return transformKeyObjectivesDataToHierarchyFormat({
      keyObjectivesSettingsList,
      programType,
      keyObjectivesFilter,
      isDataInsightTab,
    })
  }, [keyObjectivesSettingsList, programType, keyObjectivesFilter, isDataInsightTab])

  const expansionKeys = useMemo(
    () =>
      singleRootExpansion
        ? getKeyObjectiveExpansionKeys(
            keyObjectivesWithHierarchy,
            singleRootExpansion === 'leaves',
            selectedValue ?? initialValue
          )
        : NO_EXPANSION,
    [keyObjectivesWithHierarchy, singleRootExpansion, selectedValue, initialValue]
  )

  const keyObjectivesCrud = useMemo(() => {
    return new Crud({
      fetcher: new Static(keyObjectivesWithHierarchy),
      hierarchyField: 'parent',
      hierarchyRoot: null,
      store: {} as any,
      keyField: 'key',
      defaultPaging: {
        page: 0,
        pageSize: keyObjectivesWithHierarchy.length,
      },
    })
  }, [keyObjectivesWithHierarchy])

  const selectableObjectives = useMemo(
    () =>
      keyObjectivesWithHierarchy.filter((item) =>
        getIsKeyObjectiveSelectable(item, selectOnlyLeaves)
      ),
    [keyObjectivesWithHierarchy, selectOnlyLeaves]
  )

  const handleValidate = useCallback(
    (value: string) => ValidationHelper.checkDropdown(value, selectableObjectives, 'key'),
    [selectableObjectives]
  )

  const isKeyObjectiveSelectable = useCallback(
    (item: IForm) => selectableObjectives.some((objective) => objective.key === item.key),
    [selectableObjectives]
  )

  const handleNonSelectableParentClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation()

    // The dropdown checks itemClickCallback before its built-in row expansion.
    // Delegate the label click to the caret so a non-selectable parent still expands.
    const row = event.currentTarget.closest('[data-testid^="table-row-"]')
    const hierarchyButton = row?.querySelector<HTMLButtonElement>(
      '[data-testid^="select-hierarchy-button-"]'
    )

    if (hierarchyButton?.querySelector('.kbase-caret-right')) {
      hierarchyButton.click()
    }
  }, [])

  return (
    <FormDropdown
      hasHierarchy
      isStatic
      isRequired
      formKey={formKey}
      field="keyObjective"
      defaultValue={initialValue}
      classes={{ controlClasses: { root: className } }}
      source={keyObjectivesCrud}
      caption={caption ?? 'key'}
      label={label}
      placeholder={placeholder}
      isDisabled={isDisabled || isAmplifyImpactInfoSection}
      itemClickCallback={isKeyObjectiveSelectable}
      onChange={onChange}
      onValidate={handleValidate}
      validationType={ValidationType.ONCHANGE}
      renderFunction={(el) => {
        const isSelectable = isKeyObjectiveSelectable(el)

        return (
          <KeyObjectiveItem
            item={el}
            isSelectable={isSelectable}
            expansionKeys={el.key === expansionKeys[0] ? expansionKeys : NO_EXPANSION}
            onParentClick={handleNonSelectableParentClick}
          />
        )
      }}
    />
  )
}

KeyObjectives.displayName = 'KeyObjectives'
